import React from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, SelectProps } from 'antd';
import { t } from 'i18next';
import { useSelector } from 'react-redux';

import { HoursSelector } from 'shared/FormItems/HoursSelector';

import { selectWorkTime } from 'features/config';

import { TimePriceFormProps, RequiredFormValues } from '../types';


type Props<FormValues> = {
  selectProps?: SelectProps;
  formProps: TimePriceFormProps<FormValues>;
};

export const SelectTimePrice = <FormValues extends RequiredFormValues,>
  ({
    formProps: { initialValue: formInitialValue, ...formProps }
  }: Props<FormValues>) => {
  const formTimesPrice = Form.useWatch(['metainfo', 'pricing'], formProps.form);
  const workTime = useSelector(selectWorkTime);
  const initialValue = formInitialValue ?? [{
    startTime: workTime.open,
    endTime: workTime.closed,
    price: 1,
  }];
  const timesPrice = formTimesPrice ?? initialValue;
  return (
    <Form.List
      {...formProps}
      name={['metainfo', 'pricing']}
      {...(formInitialValue === undefined && { initialValue })}
    >
      {(limitsFields, { add: addLimit, remove: removeLimit }) => (
        <>
          {limitsFields.map(({ name, ...restField }, fieldIndex) => {
            const currentTimePrice = timesPrice[fieldIndex];
            const priceStartTimeFrom = fieldIndex === 0
              ? workTime.open : timesPrice[fieldIndex - 1].endTime;
            const priceStartTimeTo = currentTimePrice.endTime - 1;
            const priceOverTimeFrom = currentTimePrice.startTime + 1;
            const priceOverTimeTo = workTime.closed;
            const isThisTimePriceActive = fieldIndex === timesPrice.length - 1;

            return (
              <div key={fieldIndex}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <HoursSelector
                    from={priceStartTimeFrom}
                    to={priceStartTimeTo}
                    formProps={{
                      ...restField,
                      name: [name, 'startTime'],
                      label: t('form-items.select.time-space.start'),
                      style: { width: '45%' }
                    }}
                    selectProps={{
                      disabled: !isThisTimePriceActive,
                    }}
                  />
                  <HoursSelector
                    from={priceOverTimeFrom}
                    to={priceOverTimeTo}
                    formProps={{
                      ...restField,
                      name: [name, 'endTime'],
                      label: t('form-items.select.time-space.end'),
                      style: { width: '45%' }
                    }}
                    selectProps={{
                      disabled: !isThisTimePriceActive,
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    marginBottom: '16px'
                  }}
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'price']}
                    label={t('form-items.input.hour-price')}
                  >
                    <InputNumber min={1} max={100000} />
                  </Form.Item>
                  {
                    fieldIndex === limitsFields.length - 1 &&
                    fieldIndex !== 0 &&
                    <Button
                      onClick={() => removeLimit(name)}
                      style={{ marginBottom: '5px' }}
                      danger
                    >
                      {t('form-items.btn.delete-times-price')}
                    </Button>
                  }
                </div>
              </div>
            );
          })}
          {
            timesPrice &&
            timesPrice[timesPrice.length - 1].endTime < workTime.closed &&
            (
              <Form.Item style={{ marginTop: '8px' }}>
                <Button type="dashed" onClick={() => addLimit({ startTime: timesPrice[timesPrice.length - 1].endTime, endTime: workTime.closed, price: 1 })} block icon={<PlusOutlined />}>
                  {t('form-items.btn.add-times-price')}
                </Button>
              </Form.Item>
            )
          }
        </>
      )}
    </Form.List >
  );
};
